import { HttpClient } from '@shared/adapters/HttpClient';
import {
  CartsResponse,
  CreateLocationOrderRequest,
  CreateLocationOrderResponse,
  IdentifiedOrder,
  IdentifyOrderRequest,
  LocationOrdersResponse,
  ProcessedOrdersResponse,
  ReceivedLinesResponse,
  ReceiveOrderRequest,
  ReturnedReception,
} from '../models';

export interface GetReceivedLinesParams {
  page?: number;
  per_page?: number;
  product_id?: number;
  order_id?: number;
  status?: 'ACEPTADO' | 'RECHAZADO';
  rejection_reason?: string;
  order_by?: string;
  order_direction?: 'asc' | 'desc';
}

export interface GetProcessedOrdersParams {
  order_id?: number;
  has_courier_incidence?: boolean;
  from_date?: string;
  to_date?: string;
  page?: number;
  per_page?: number;
}

export interface GetLocationOrdersParams {
  status?: 'pending' | 'in_progress' | 'done' | 'cancelled';
  order_type?: string;
  team_id?: number;
  from_date?: string;
  to_date?: string;
  order_by?: string;
  order_direction?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}

export class ReceiveApiAdapter {
  constructor(private httpClient: HttpClient) {}

  /**
   * Obtiene una recepción específica por ID
   */
  async getReception(id: number): Promise<{ data: ReturnedReception }> {
    return await this.httpClient.get<{ data: ReturnedReception }>(`/returned-receptions/${id}`);
  }

  /**
   * Identifica un pedido por código de barras
   */
  async identifyOrder(data: IdentifyOrderRequest): Promise<IdentifiedOrder> {
    return await this.httpClient.post<IdentifiedOrder>('/returned-receptions/identify-order', data);
  }

  /**
   * Recepciona un pedido devuelto
   */
  async receiveOrder(receptionId: number, data: ReceiveOrderRequest): Promise<{ message: string }> {
    return await this.httpClient.post<{ message: string }>(`/returned-receptions/${receptionId}/receive`, data);
  }

  /**
   * Recepciona un pedido devuelto con archivos (multipart/form-data)
   */
  async receiveOrderMultipart(receptionId: number, data: ReceiveOrderRequest): Promise<{ message: string }> {
    const formData = new FormData();
    formData.append('order_id', String(data.order_id));
    formData.append('cart_id', String(data.cart_id));
    // El backend espera 0/1 en multipart para evitar interpretar "false"/"true" como string
    formData.append('has_courier_incidence', data.has_courier_incidence ? '1' : '0');

    if (data.incidence_type) {
      formData.append('incidence_type', data.incidence_type);
    }

    data.lines.forEach((line, index) => {
      formData.append(`lines[${index}][product_id]`, String(line.product_id));
      formData.append(`lines[${index}][quantity]`, String(line.quantity));
      formData.append(`lines[${index}][status]`, line.status);

      if (line.rejection_reason) {
        formData.append(`lines[${index}][rejection_reason]`, line.rejection_reason);
      }

      if (line.stock_location_order_id !== null) {
        formData.append(`lines[${index}][location_order_id]`, String(line.stock_location_order_id));
      }

      if (line.batch_id) {
        formData.append(`lines[${index}][batch_id]`, String(line.batch_id));
      }

      if (line.rejected_imgs && line.rejected_imgs.length > 0) {
        line.rejected_imgs.forEach((file, imageIndex) => {
          formData.append(`lines[${index}][rejected_imgs][${imageIndex}]`, file);
        });
      }
    });

    return await this.httpClient.post<{ message: string }>(`/returned-receptions/${receptionId}/receive`, formData);
  }

  /**
   * Obtiene las líneas de recepción con paginación y filtros
   */
  async getReceivedLines(receptionId: number, params?: GetReceivedLinesParams): Promise<ReceivedLinesResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    if (params?.product_id) queryParams.append('product_id', params.product_id.toString());
    if (params?.order_id) queryParams.append('order_id', params.order_id.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.rejection_reason) queryParams.append('rejection_reason', params.rejection_reason);
    if (params?.order_by) queryParams.append('order_by', params.order_by);
    if (params?.order_direction) queryParams.append('order_direction', params.order_direction);

    const queryString = queryParams.toString();
    const url = `/returned-receptions/${receptionId}/lines${queryString ? `?${queryString}` : ''}`;

    return await this.httpClient.get<ReceivedLinesResponse>(url);
  }

  /**
   * Obtiene los pedidos procesados de una recepción
   */
  async getProcessedOrders(receptionId: number, params?: GetProcessedOrdersParams): Promise<ProcessedOrdersResponse> {
    const queryParams = new URLSearchParams();

    if (params?.order_id) queryParams.append('order_id', params.order_id.toString());
    if (params?.has_courier_incidence !== undefined)
      queryParams.append('has_courier_incidence', params.has_courier_incidence.toString());
    if (params?.from_date) queryParams.append('from_date', params.from_date);
    if (params?.to_date) queryParams.append('to_date', params.to_date);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());

    const queryString = queryParams.toString();
    const url = `/returned-receptions/${receptionId}/orders${queryString ? `?${queryString}` : ''}`;

    return await this.httpClient.get<ProcessedOrdersResponse>(url);
  }

  /**
   * Obtiene las órdenes de ubicación relacionadas con una recepción
   */
  async getLocationOrders(receptionId: number, params?: GetLocationOrdersParams): Promise<LocationOrdersResponse> {
    const queryParams = new URLSearchParams();

    if (params?.status) queryParams.append('status', params.status);
    if (params?.order_type) queryParams.append('order_type', params.order_type);
    if (params?.team_id) queryParams.append('team_id', params.team_id.toString());
    if (params?.from_date) queryParams.append('from_date', params.from_date);
    if (params?.to_date) queryParams.append('to_date', params.to_date);
    if (params?.order_by) queryParams.append('order_by', params.order_by);
    if (params?.order_direction) queryParams.append('order_direction', params.order_direction);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());

    const queryString = queryParams.toString();
    const url = `/returned-receptions/${receptionId}/location-orders${queryString ? `?${queryString}` : ''}`;

    return await this.httpClient.get<LocationOrdersResponse>(url);
  }

  async createLocationOrder(
    receptionId: number,
    data: CreateLocationOrderRequest,
  ): Promise<CreateLocationOrderResponse> {
    return await this.httpClient.post<CreateLocationOrderResponse>(
      `/returned-receptions/${receptionId}/create-location-order`,
      data,
    );
  }

  async finalizeReception(receptionId: number): Promise<ReturnedReception> {
    return await this.httpClient.put<ReturnedReception>(`/returned-receptions/${receptionId}/finalize`, {});
  }

  async getCarts(receptionId: number): Promise<CartsResponse> {
    return await this.httpClient.get<CartsResponse>(`/returned-receptions/${receptionId}/carts`);
  }
}
